import { useState } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/festival-compass.css';

const DESTINATIONS = [
  { to: '/lineup', label: 'Lineup', description: 'Explore artists and choose your sets.', icon: 'M4 7h16M4 12h12M4 17h8', tone: 'var(--accent-cool)' },
  { to: '/plan', label: 'My plan', description: 'Build and adjust your festival schedule.', icon: 'M5 5h14v15H5zM8 3v4m8-4v4M5 10h14m-11 4h3m2 0h3m-8 3h3', tone: 'var(--accent)' },
  { to: '/search', label: 'People', description: 'Find shared plans and invite your friends.', icon: 'M14 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM5 20v-3a6 6 0 0 1 12 0v3m1-15a3 3 0 0 1 0 6m2 9v-3a5 5 0 0 0-2-4', tone: 'var(--gold)' },
  { to: '/forum', label: 'Community', description: 'Read discussions and share what you know.', icon: 'M4 4h16v12H10l-6 4V4Zm4 4h8m-8 4h5', tone: 'var(--accent-cool)' },
  { to: '/news', label: 'News', description: 'Follow the latest reports and official updates.', icon: 'M5 4h12v16H5zM17 9h3v9a2 2 0 0 1-2 2M8 8h6m-6 4h6m-6 4h6', tone: 'var(--accent)' },
  { to: '/weather', label: 'Weather', description: 'Check venue forecasts, alerts, and live radar.', icon: 'M8 15a4 4 0 1 1 1-8 5 5 0 0 1 9 2 3 3 0 0 1 0 6H8Zm1 3-1 3m6-3-1 3m6-3-1 3', tone: 'var(--gold)' },
] as const;

// Lithorium's placeLabels geometry, normalized to a responsive square.
const TOP = -Math.PI / 2;
const LABEL_RADIUS = 50 * 0.84;
const DIRECTIONS = DESTINATIONS.map((destination, index) => {
  const angle = TOP + index * (Math.PI * 2 / DESTINATIONS.length);
  return {
    ...destination,
    x: 50 + Math.cos(angle) * LABEL_RADIUS,
    y: 50 + Math.sin(angle) * LABEL_RADIUS,
    innerX: 50 + Math.cos(angle) * 22,
    innerY: 50 + Math.sin(angle) * 22,
  };
});

export function FestivalCompass({ className = '' }: { className?: string }) {
  const [listView, setListView] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const selected = DIRECTIONS.find(direction => direction.to === (focused ?? hovered));

  return (
    <section className={`festival-compass ${listView ? 'festival-compass--list' : ''} ${className}`}>
      <nav className="festival-compass__wheel" aria-label="Festival destinations">
        <svg className="festival-compass__rings" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
          <circle cx="50" cy="50" r="42" />
          <circle className="festival-compass__inner-ring" cx="50" cy="50" r="31" />
          {DIRECTIONS.map(direction => (
            <line key={direction.to} x1={direction.innerX} y1={direction.innerY} x2={direction.x} y2={direction.y}
              className={selected?.to === direction.to ? 'is-selected' : ''}
              style={{ '--direction-tone': direction.tone } as CSSProperties} />
          ))}
          <path className="festival-compass__needle" d="M50 26l-2 7h4ZM50 74l-2-7h4Z" />
        </svg>

        <div className="festival-compass__center" aria-hidden="true">
          <span className="festival-compass__year">2026</span>
          <strong>Lost<br />Lands</strong>
          <span className="festival-compass__place">Legend Valley</span>
        </div>

        {DIRECTIONS.map(direction => (
          <Link key={direction.to} to={direction.to}
            className={`festival-compass__link ${selected?.to === direction.to ? 'is-selected' : ''}`}
            style={{ '--direction-x': `${direction.x}%`, '--direction-y': `${direction.y}%`, '--direction-tone': direction.tone } as CSSProperties}
            aria-label={`${direction.label}: ${direction.description}`}
            onPointerEnter={event => { if (event.pointerType !== 'touch') setHovered(direction.to); }}
            onPointerLeave={() => setHovered(null)}
            onFocus={() => setFocused(direction.to)}
            onBlur={() => setFocused(null)}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
              <path d={direction.icon} />
            </svg>
            <span>{direction.label}</span>
          </Link>
        ))}
      </nav>

      <p className="festival-compass__context" aria-hidden="true">
        {selected ? selected.description : 'Lineup, plans, people, and updates.'}
      </p>
      <button type="button" className="festival-compass__view" aria-pressed={listView}
        onClick={() => { setListView(current => !current); setHovered(null); setFocused(null); }}>
        <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false">
          <path d="M3 5h2m3 0h9M3 10h2m3 0h9M3 15h2m3 0h9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <span>List view</span>
      </button>
    </section>
  );
}
